"use client";

import { PlayIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";

interface PlayAllButtonProps {
  label: string;
  onClick: () => void;
  canPlayAll: boolean;
  isPlayAllResolving?: boolean;
  unavailableLabel: string;
  /** Id of the tracklist section to bring into view once playback starts. */
  scrollTargetId?: string;
  className?: string;
}

export function PlayAllButton({
  label,
  onClick,
  canPlayAll,
  isPlayAllResolving = false,
  unavailableLabel,
  scrollTargetId,
  className,
}: PlayAllButtonProps) {
  const disabled = !canPlayAll;
  const resolvedLabel = disabled && !isPlayAllResolving ? unavailableLabel : label;

  const handleClick = () => {
    onClick();
    if (!scrollTargetId) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document
      .getElementById(scrollTargetId)
      ?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-busy={isPlayAllResolving || undefined}
      className={cn(
        "flex min-h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-bold text-primary transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:border-border disabled:bg-secondary/70 disabled:text-muted-foreground md:flex-none md:px-4",
        isPlayAllResolving && "animate-pulse",
        className,
      )}
    >
      <PlayIcon className="shrink-0 text-lg" />
      <span className="truncate">{resolvedLabel}</span>
    </button>
  );
}
